import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, 
    doc, 
    getDoc,
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc,
    onSnapshot,
    query,
    where,
    orderBy,
    arrayUnion,
    increment,
    serverTimestamp
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import './Projects.css';

const Projects = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showDonationModal, setShowDonationModal] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [currentDonatingId, setCurrentDonatingId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [contributions, setContributions] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        location: '',
        manager: '',
        status: 'planned',
        priority: 'medium',
        targetAmount: '',
        currentAmount: '0',
        startDate: '',
        endDate: '',
        description: '',
        imageUrl: '',
        cloudinaryPublicId: ''
    });

    // Donation form state
    const [donationData, setDonationData] = useState({
        donorName: '',
        amount: '',
        message: ''
    });

    // Cloudinary widget reference
    const cloudinaryWidgetRef = useRef(null);

    // Cloudinary configuration
    const cloudinaryConfig = {
        cloudName: 'dblffn98e',
        uploadPreset: 'WHITE DOVES MINISTRY',
        apiKey: '478415172259589',
    };

    useEffect(() => {
        loadProjects();
        initializeCloudinaryWidget();

        // Set donor name to user email if available
        if (currentUser) {
            setDonationData(prev => ({ ...prev, donorName: currentUser.email }));
        }
    }, [statusFilter]);

    useEffect(() => {
        if (isAdmin) {
            loadContributions();
        }
    }, [isAdmin]);

    const initializeCloudinaryWidget = () => {
        if (window.cloudinary) {
            cloudinaryWidgetRef.current = window.cloudinary.createUploadWidget({
                cloudName: cloudinaryConfig.cloudName,
                uploadPreset: cloudinaryConfig.uploadPreset,
                sources: ['local', 'url', 'camera'],
                multiple: false,
                maxFileSize: 5000000,
                maxImageWidth: 2000,
                maxImageHeight: 2000,
                cropping: true,
                croppingAspectRatio: 1.78,
                showAdvancedOptions: false,
                styles: {
                    palette: {
                        window: "#FFFFFF",
                        windowBorder: "#90A0B3",
                        tabIcon: "#0078FF",
                        menuIcons: "#5A616A",
                        textDark: "#000000",
                        textLight: "#FFFFFF",
                        link: "#0078FF",
                        action: "#FF620C",
                        inactiveTabIcon: "#0E2F5A",
                        error: "#F44235",
                        inProgress: "#0078FF",
                        complete: "#20B832",
                        sourceBg: "#E4EBF1"
                    }
                }
            }, (error, result) => {
                if (!error && result && result.event === "success") {
                    setFormData(prev => ({
                        ...prev,
                        imageUrl: result.info.secure_url,
                        cloudinaryPublicId: result.info.public_id
                    }));
                    showToast('Image uploaded successfully!', 'success');
                } else if (error) {
                    showToast('Image upload failed', 'error');
                }
            });
        }
    };

    const loadProjects = () => {
        setLoading(true);
        
        let projectsQuery;
        if (statusFilter !== 'all') {
            projectsQuery = query(
                collection(db, 'projects'),
                where('status', '==', statusFilter),
                orderBy('startDate', 'desc')
            );
        } else {
            projectsQuery = query(
                collection(db, 'projects'),
                orderBy('startDate', 'desc')
            );
        }

        const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
            const projectsData = [];
            snapshot.forEach(doc => {
                projectsData.push({ id: doc.id, ...doc.data() });
            });
            setProjects(projectsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading projects:', error);
            showToast('Error loading projects', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const loadContributions = async () => {
        try {
            const contributionsQuery = query(
                collection(db, 'contributions'),
                orderBy('donatedAt', 'desc')
            );
            
            const querySnapshot = await getDocs(contributionsQuery);
            const contributionsData = [];
            
            querySnapshot.forEach(doc => {
                contributionsData.push({ id: doc.id, ...doc.data() });
            });
            
            setContributions(contributionsData);
        } catch (error) {
            console.error('Error loading contributions:', error);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleDonationChange = (e) => {
        const { id, value } = e.target;
        setDonationData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            category: '',
            location: '',
            manager: '',
            status: 'planned',
            priority: 'medium',
            targetAmount: '',
            currentAmount: '0',
            startDate: '',
            endDate: '',
            description: '',
            imageUrl: '',
            cloudinaryPublicId: ''
        });
        setEditingProject(null);
    };

    const resetDonationForm = () => {
        setDonationData({
            donorName: currentUser?.email || '',
            amount: '',
            message: ''
        });
        setCurrentDonatingId(null);
    };

    const openCloudinaryWidget = () => {
        if (cloudinaryWidgetRef.current) {
            cloudinaryWidgetRef.current.open();
        }
    };

    const validateForm = () => {
        if (!formData.title || !formData.location || !formData.manager || !formData.targetAmount || !formData.startDate || !formData.endDate || !formData.description) {
            showToast('Please fill in all required fields', 'error');
            return false;
        }

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            showToast('End date must be after start date', 'error');
            return false;
        }

        const target = parseFloat(formData.targetAmount);
        const current = parseFloat(formData.currentAmount);

        if (current > target) {
            showToast('Current amount cannot exceed target amount', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAdmin) {
            showToast('Only administrators can manage projects', 'error');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const targetAmount = parseFloat(formData.targetAmount);
            const currentAmount = parseFloat(formData.currentAmount);
            const status = currentAmount >= targetAmount ? 'funded' : formData.status;

            const projectData = {
                title: formData.title,
                category: formData.category,
                location: formData.location,
                manager: formData.manager,
                status: status,
                priority: formData.priority,
                targetAmount: targetAmount,
                currentAmount: currentAmount,
                startDate: formData.startDate,
                endDate: formData.endDate,
                description: formData.description,
                imageUrl: formData.imageUrl,
                cloudinaryPublicId: formData.cloudinaryPublicId,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid
            };

            if (editingProject) {
                // Update existing project
                const projectRef = doc(db, 'projects', editingProject.id);
                await updateDoc(projectRef, projectData);
                showToast('Project updated successfully!', 'success');
            } else {
                // Create new project
                projectData.createdAt = serverTimestamp();
                projectData.createdBy = currentUser.uid;
                projectData.viewCount = 0;
                projectData.donationCount = 0;
                projectData.viewedUsers = [];

                await addDoc(collection(db, 'projects'), projectData);
                showToast('Project added successfully!', 'success');
            }

            setShowProjectModal(false);
            resetForm();
        } catch (error) {
            console.error('Error saving project:', error);
            showToast('Error saving project', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        setFormData({
            title: project.title || '',
            category: project.category || '',
            location: project.location || '',
            manager: project.manager || '',
            status: project.status || 'planned',
            priority: project.priority || 'medium',
            targetAmount: project.targetAmount?.toString() || '',
            currentAmount: project.currentAmount?.toString() || '0',
            startDate: project.startDate || '',
            endDate: project.endDate || '',
            description: project.description || '',
            imageUrl: project.imageUrl || '',
            cloudinaryPublicId: project.cloudinaryPublicId || ''
        });
        setShowProjectModal(true);
    };

    const handleDelete = async (projectId) => {
        if (!isAdmin) {
            showToast('Only administrators can delete projects', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Project?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'projects', projectId));
                showToast('Project deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting project:', error);
                showToast('Error deleting project', 'error');
            }
        }
    };

    const handleDonate = (projectId) => {
        setCurrentDonatingId(projectId);
        setDonationData(prev => ({ ...prev, donorName: currentUser?.email || '' }));
        setShowDonationModal(true);
    };

    const processDonation = async () => {
        const amount = parseFloat(donationData.amount);
        
        if (!amount || amount <= 0) {
            showToast('Please enter a valid donation amount', 'error');
            return;
        }

        setLoading(true);

        try {
            const projectRef = doc(db, 'projects', currentDonatingId);
            
            // Update project amount and donation count
            await updateDoc(projectRef, {
                currentAmount: increment(amount),
                donationCount: increment(1)
            });

            // Get project title for record
            const projectSnap = await getDoc(projectRef);
            const projectTitle = projectSnap.exists() ? projectSnap.data().title : 'Unknown Project';

            // Add donation record
            await addDoc(collection(db, 'contributions'), {
                projectId: currentDonatingId,
                projectTitle: projectTitle,
                userId: currentUser.uid,
                userEmail: currentUser.email,
                donorName: donationData.donorName || currentUser.email,
                amount: amount,
                message: donationData.message,
                donatedAt: serverTimestamp()
            });

            showToast(`Thank you for your donation of Ksh ${amount.toFixed(2)}!`, 'success');
            
            setShowDonationModal(false);
            resetDonationForm();
            
            // Reload contributions if admin
            if (isAdmin) {
                loadContributions();
            }
        } catch (error) {
            console.error('Error processing donation:', error);
            showToast('Failed to process donation', 'error');
        } finally {
            setLoading(false);
        }
    };

    const trackProjectView = async (projectId) => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            
            if (projectSnap.exists()) {
                const project = projectSnap.data();
                if (!project.viewedUsers?.includes(currentUser.uid)) {
                    await updateDoc(projectRef, {
                        viewedUsers: arrayUnion(currentUser.uid),
                        viewCount: increment(1)
                    });
                }
            }
        } catch (error) {
            console.error('Error tracking project view:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const shareProject = (project) => {
        const shareText = `Check out this project: ${project.title}\n\n${project.description}\n\nLocation: ${project.location}\nTarget: ${formatCurrency(project.targetAmount)}\nRaised: ${formatCurrency(project.currentAmount)}`;
        
        if (navigator.share) {
            navigator.share({
                title: project.title,
                text: shareText,
                url: window.location.href
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(shareText).then(() => {
                showToast('Project details copied to clipboard!', 'success');
            });
        }
    };

    const filteredProjects = projects.filter(project => {
        const searchableText = `${project.title} ${project.location} ${project.manager} ${project.description} ${project.category || ''}`.toLowerCase();
        return searchableText.includes(searchTerm.toLowerCase());
    });

    const stats = {
        total: projects.length,
        active: projects.filter(p => p.status === 'active').length,
        planned: projects.filter(p => p.status === 'planned').length,
        completed: projects.filter(p => p.status === 'completed').length,
        funded: projects.filter(p => p.status === 'funded').length,
        totalRaised: projects.reduce((sum, p) => sum + (parseFloat(p.currentAmount) || 0), 0)
    };

    if (loading && projects.length === 0) {
        return (
            <div className="projects-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading projects...</p>
            </div>
        );
    }

    return (
        <div className="projects-page">
            <div className="projects-header">
                <div>
                    <h1 className="projects-title">Ministry Projects</h1>
                    <p className="projects-subtitle">Support our projects and help make a difference</p>
                </div>
                <div className="projects-controls">
                    <div className="projects-search">
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="projects-search-input"
                        />
                        <i className="fas fa-search projects-search-icon"></i>
                    </div>
                    <select
                        className="projects-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Projects</option>
                        <option value="planned">Planned</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="funded">Fully Funded</option>
                    </select>
                    {isAdmin && (
                        <button
                            className="projects-add-btn"
                            onClick={() => {
                                resetForm();
                                setShowProjectModal(true);
                            }}
                        >
                            <i className="fas fa-plus"></i> Add Project
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="projects-stats">
                <div className="projects-stat-card">
                    <div className="projects-stat-icon total">
                        <i className="fas fa-project-diagram"></i>
                    </div>
                    <div>
                        <div className="projects-stat-value">{stats.total}</div>
                        <div className="projects-stat-label">Total Projects</div>
                    </div>
                </div>
                <div className="projects-stat-card">
                    <div className="projects-stat-icon active">
                        <i className="fas fa-play-circle"></i>
                    </div>
                    <div>
                        <div className="projects-stat-value">{stats.active}</div>
                        <div className="projects-stat-label">Active</div>
                    </div>
                </div>
                <div className="projects-stat-card">
                    <div className="projects-stat-icon planned">
                        <i className="fas fa-clock"></i>
                    </div>
                    <div>
                        <div className="projects-stat-value">{stats.planned}</div>
                        <div className="projects-stat-label">Planned</div>
                    </div>
                </div>
                <div className="projects-stat-card">
                    <div className="projects-stat-icon funded">
                        <i className="fas fa-hand-holding-usd"></i>
                    </div>
                    <div>
                        <div className="projects-stat-value">{formatCurrency(stats.totalRaised)}</div>
                        <div className="projects-stat-label">Total Raised</div>
                    </div>
                </div>
            </div>

            {/* Projects Grid */}
            <div className="projects-grid">
                {filteredProjects.length === 0 ? (
                    <div className="projects-empty">
                        <i className="fas fa-project-diagram"></i>
                        <h3>No Projects Found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                ) : (
                    filteredProjects.map(project => {
                        const progress = Math.min(
                            Math.round((parseFloat(project.currentAmount || 0) / parseFloat(project.targetAmount || 1)) * 100),
                            100
                        );
                        const isFunded = progress >= 100 || project.status === 'funded';

                        return (
                            <div
                                key={project.id}
                                className={`projects-card priority-${project.priority}`}
                                onClick={() => trackProjectView(project.id)}
                            >
                                <div className="projects-image-container">
                                    {project.imageUrl ? (
                                        <img
                                            src={project.imageUrl}
                                            alt={project.title}
                                            className="projects-image"
                                        />
                                    ) : (
                                        <div className="projects-no-image">
                                            <i className="fas fa-project-diagram"></i>
                                        </div>
                                    )}
                                    <span className={`projects-status-badge status-${project.status}`}>
                                        {project.status}
                                    </span>
                                </div>

                                <div className="projects-content">
                                    {project.category && (
                                        <div className="projects-category">{project.category}</div>
                                    )}
                                    <h3 className="projects-title">{project.title}</h3>

                                    <div className="projects-details">
                                        <div className="projects-detail">
                                            <i className="fas fa-map-marker-alt"></i>
                                            <span>{project.location}</span>
                                        </div>
                                        <div className="projects-detail">
                                            <i className="fas fa-user-tie"></i>
                                            <span>Manager: {project.manager}</span>
                                        </div>
                                        <div className="projects-detail">
                                            <i className="fas fa-calendar-alt"></i>
                                            <span>
                                                {formatDate(project.startDate)} - {formatDate(project.endDate)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="projects-progress">
                                        <div className="projects-progress-info">
                                            <span>Funding Progress</span>
                                            <span>{progress}% ({formatCurrency(project.currentAmount)} / {formatCurrency(project.targetAmount)})</span>
                                        </div>
                                        <div className="projects-progress-bar">
                                            <div
                                                className={`projects-progress-fill ${isFunded ? 'funded' : ''}`}
                                                style={{ width: `${progress}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <p className="projects-description">{project.description}</p>

                                    <div className="projects-stats">
                                        <div className="projects-stat-item">
                                            <i className="fas fa-eye"></i>
                                            <span>{project.viewCount || 0} views</span>
                                        </div>
                                        <div className="projects-stat-item">
                                            <i className="fas fa-hand-holding-usd"></i>
                                            <span>{project.donationCount || 0} donations</span>
                                        </div>
                                    </div>

                                    <div className="projects-actions">
                                        <button
                                            className="projects-share-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                shareProject(project);
                                            }}
                                        >
                                            <i className="fas fa-share-alt"></i> Share
                                        </button>
                                        <button
                                            className="projects-donate-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDonate(project.id);
                                            }}
                                            disabled={isFunded}
                                        >
                                            <i className="fas fa-donate"></i>
                                            {isFunded ? 'Fully Funded' : 'Donate'}
                                        </button>
                                        {isAdmin && (
                                            <>
                                                <button
                                                    className="projects-edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(project);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="projects-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(project.id);
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Admin Panel - Contributions */}
            {isAdmin && (
                <div className="projects-admin-panel">
                    <h3 className="projects-admin-title">
                        <i className="fas fa-hand-holding-usd"></i>
                        Recent Contributions
                    </h3>
                    <div className="projects-contributions-list">
                        {contributions.length === 0 ? (
                            <p className="projects-contributions-empty">No contributions yet</p>
                        ) : (
                            contributions.map((contribution, index) => (
                                <div key={index} className="projects-contribution-item">
                                    <div className="projects-contribution-info">
                                        <div className="projects-contribution-user">
                                            {contribution.donorName || contribution.userEmail}
                                        </div>
                                        <div className="projects-contribution-project">
                                            to: {contribution.projectTitle || 'Unknown Project'}
                                        </div>
                                        {contribution.message && (
                                            <div className="projects-contribution-message">
                                                "{contribution.message}"
                                            </div>
                                        )}
                                        <div className="projects-contribution-date">
                                            {formatDate(contribution.donatedAt)}
                                        </div>
                                    </div>
                                    <div className="projects-contribution-amount">
                                        {formatCurrency(contribution.amount)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Project Modal */}
            {showProjectModal && (
                <div className="projects-modal" onClick={() => setShowProjectModal(false)}>
                    <div className="projects-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="projects-modal-header">
                            <h3 className="projects-modal-title">
                                {editingProject ? 'Edit Project' : 'Add New Project'}
                            </h3>
                            <button
                                className="projects-modal-close"
                                onClick={() => setShowProjectModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="projects-form">
                            <input type="hidden" id="imageUrl" value={formData.imageUrl} />
                            <input type="hidden" id="cloudinaryPublicId" value={formData.cloudinaryPublicId} />

                            <div className="projects-form-group">
                                <label htmlFor="title">Project Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    className="projects-form-control"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="projects-form-row">
                                <div className="projects-form-group">
                                    <label htmlFor="category">Category</label>
                                    <select
                                        id="category"
                                        className="projects-form-control"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Category</option>
                                        <option value="construction">Construction</option>
                                        <option value="education">Education</option>
                                        <option value="medical">Medical</option>
                                        <option value="humanitarian">Humanitarian Aid</option>
                                        <option value="evangelism">Evangelism</option>
                                        <option value="community">Community Development</option>
                                        <option value="technology">Technology</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="projects-form-group">
                                    <label htmlFor="priority">Priority</label>
                                    <select
                                        id="priority"
                                        className="projects-form-control"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>

                            <div className="projects-form-row">
                                <div className="projects-form-group">
                                    <label htmlFor="location">Location *</label>
                                    <input
                                        type="text"
                                        id="location"
                                        className="projects-form-control"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="projects-form-group">
                                    <label htmlFor="manager">Project Manager *</label>
                                    <input
                                        type="text"
                                        id="manager"
                                        className="projects-form-control"
                                        value={formData.manager}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="projects-form-row">
                                <div className="projects-form-group">
                                    <label htmlFor="status">Status *</label>
                                    <select
                                        id="status"
                                        className="projects-form-control"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="planned">Planned</option>
                                        <option value="active">Active</option>
                                        <option value="completed">Completed</option>
                                        <option value="funded">Fully Funded</option>
                                    </select>
                                </div>

                                <div className="projects-form-group">
                                    <label htmlFor="priority">Priority</label>
                                    <select
                                        id="priority"
                                        className="projects-form-control"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>

                            <div className="projects-form-row">
                                <div className="projects-form-group">
                                    <label htmlFor="targetAmount">Target Amount (Ksh) *</label>
                                    <input
                                        type="number"
                                        id="targetAmount"
                                        className="projects-form-control"
                                        value={formData.targetAmount}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                <div className="projects-form-group">
                                    <label htmlFor="currentAmount">Current Amount (Ksh) *</label>
                                    <input
                                        type="number"
                                        id="currentAmount"
                                        className="projects-form-control"
                                        value={formData.currentAmount}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="projects-form-row">
                                <div className="projects-form-group">
                                    <label htmlFor="startDate">Start Date *</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        className="projects-form-control"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="projects-form-group">
                                    <label htmlFor="endDate">End Date *</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        className="projects-form-control"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="projects-form-group">
                                <label htmlFor="description">Description *</label>
                                <textarea
                                    id="description"
                                    className="projects-form-control"
                                    rows="4"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                ></textarea>
                            </div>

                            <div className="projects-form-group">
                                <label>Project Image</label>
                                <div
                                    className="projects-image-upload"
                                    onClick={openCloudinaryWidget}
                                >
                                    {formData.imageUrl ? (
                                        <>
                                            <img
                                                src={formData.imageUrl}
                                                alt="Preview"
                                                className="projects-image-preview"
                                            />
                                            <p className="projects-image-upload-text">
                                                <i className="fas fa-sync-alt"></i> Change Image
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-cloud-upload-alt projects-upload-icon"></i>
                                            <p>Click to upload project image to Cloudinary</p>
                                            <p className="projects-upload-hint">JPG, PNG, WebP (Max 5MB)</p>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="projects-form-actions">
                                <button
                                    type="button"
                                    className="projects-btn-secondary"
                                    onClick={() => setShowProjectModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="projects-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingProject ? 'Update Project' : 'Create Project'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Donation Modal */}
            {showDonationModal && (
                <div className="projects-modal" onClick={() => setShowDonationModal(false)}>
                    <div className="projects-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="projects-modal-header">
                            <h3 className="projects-modal-title">
                                Support This Project
                            </h3>
                            <button
                                className="projects-modal-close"
                                onClick={() => setShowDonationModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="projects-donation-form">
                            <div className="projects-form-group">
                                <label htmlFor="donorName">Your Name</label>
                                <input
                                    type="text"
                                    id="donorName"
                                    className="projects-form-control"
                                    value={donationData.donorName}
                                    onChange={handleDonationChange}
                                />
                            </div>

                            <div className="projects-form-group">
                                <label htmlFor="amount">Donation Amount (Ksh) *</label>
                                <div className="projects-donation-input-group">
                                    <input
                                        type="number"
                                        id="amount"
                                        className="projects-form-control"
                                        value={donationData.amount}
                                        onChange={handleDonationChange}
                                        min="1"
                                        step="0.01"
                                        required
                                    />
                                </div>
                                
                                <div className="projects-quick-amounts">
                                    <button
                                        type="button"
                                        className="projects-quick-amount"
                                        onClick={() => setDonationData(prev => ({ ...prev, amount: '100' }))}
                                    >
                                        Ksh 100
                                    </button>
                                    <button
                                        type="button"
                                        className="projects-quick-amount"
                                        onClick={() => setDonationData(prev => ({ ...prev, amount: '250' }))}
                                    >
                                        Ksh 250
                                    </button>
                                    <button
                                        type="button"
                                        className="projects-quick-amount"
                                        onClick={() => setDonationData(prev => ({ ...prev, amount: '500' }))}
                                    >
                                        Ksh 500
                                    </button>
                                    <button
                                        type="button"
                                        className="projects-quick-amount"
                                        onClick={() => setDonationData(prev => ({ ...prev, amount: '1000' }))}
                                    >
                                        Ksh 1,000
                                    </button>
                                    <button
                                        type="button"
                                        className="projects-quick-amount"
                                        onClick={() => setDonationData(prev => ({ ...prev, amount: '5000' }))}
                                    >
                                        Ksh 5,000
                                    </button>
                                </div>
                            </div>

                            <div className="projects-form-group">
                                <label htmlFor="message">Message (Optional)</label>
                                <textarea
                                    id="message"
                                    className="projects-form-control"
                                    rows="3"
                                    placeholder="Add a message of encouragement..."
                                    value={donationData.message}
                                    onChange={handleDonationChange}
                                ></textarea>
                            </div>

                            <div className="projects-form-actions">
                                <button
                                    type="button"
                                    className="projects-btn-secondary"
                                    onClick={() => setShowDonationModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="projects-btn-success"
                                    onClick={processDonation}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-donate"></i>
                                            Donate Now
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projects;
